import { ActionReference, DefaultGameState, GameObjectReference, GameState } from "@shared/types";
import { css, html, htmlArray } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";
import { Page } from "../enums/Page";

/** CSS affecting the {@link CanvasComponent} */
const styles: string = css`
    :host(.page-startup) {
        grid-template-rows: 1fr;
        align-items: center;
        justify-content: center;
    }

    :host(.page-startup) .header img {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        object-fit: cover;
        object-position: center 40%; 
        z-index: -1; 
        margin-top: 0;
    }

    :host(.page-einde) {
        grid-template-rows: auto auto auto 0px; 
        align-content: start; 
        justify-content: center;
    }

    :host(.page-einde) .title {
        margin-top: 80px;
    }

    :host(.page-einde) .content {
        margin-top: 20px; 
        height: fit-content;
        padding-bottom: 50px;
        user-select: none;
        overflow: visible;
        display: block;
    }

    :host(.page-einde) .content p {
        font-size: 14px;
    }

    .btn-start-game {
        background-color: #262626;
        border: none;
        color: #fff;
        width: 300px;
        height: 50px;
        font-size: 24px;
        font-weight: 700;
        cursor: pointer;
        border-radius: 5px;
        transition: all 0.3s ease;
        margin-bottom: 50px;
    }

    .btn-start-game:hover {
        background-color: #313131;
    }

    :host(.page-startup) .footer,
    :host(.page-startup) .title {
        display: none;
    }
    
    :host {
        font-family: "Inter";
        width: 100%;
        height: 100%;
        display: grid;
        grid-template-columns: 1fr;
        grid-template-rows: auto calc(39vh) minmax(calc(25vh + 10px), 1fr) auto;
        grid-column-gap: 0px;
        grid-row-gap: 0px;
        position: relative;
        user-select: none;
    }

    .title {
        text-align: center;
        overflow: auto;
        font-weight: 700;
        max-width: 70%;
        margin-left: auto;
        margin-right: auto;
        margin-top: 80px;
    }

    .header {
        display: flex;
        flex-direction: column;
        align-items: center;
        position: relative;
    }

    .header img {
        margin-top: 15px;
        width: 50%;
        height: auto;
        object-fit: contain;
    }

    .header video {
        margin-top: 15px;
        width: 50%;
        height: auto;
        object-fit: contain;
    }

    .header img:nth-child(n + 2) {
        position: absolute;
    }

    .content {
        text-align: center;
        flex-grow: 1;
        overflow: auto;
        margin-top: 20px;
        padding: 0 10px;
        margin-left: auto;
        margin-right: auto;
    }

    .content p {
        margin: 0 0 5px 0;
        font-size: 14px;
    }

    .content p:last-of-type {
        margin: 0;
    }

    .footer {
        border-radius: 25px 25px 0 0;
        background-color: #313131;
        height: 350px;
        width: 264px;
        position: absolute;
        bottom: 0;
        right: 20vw;
        cursor: pointer;
    }

    .footer .buttons {
        display: flex;
        flex-direction: column;
        overflow: auto;
    }

    .footer .button {
        display: block;
        border-radius: 10px;
        background-color: #262626;
        border: 1px solid #A9A9A9;
        padding: 10px 15px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        user-select: none;
        color: #A9A9A9;
        text-align: left;
    }

    .footer .button.active,
    .footer .button:hover {
        background-color: #A9A9A9;
        color: #262626;
    }

    .footer .buttons > div:first-child {
        display: flex;
        flex-direction: row;
        gap: 12px;
        margin: 16px;
    }

    .footer .buttons > div:last-child {
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 0 16px 16px 16px;
        overflow-y: auto;
    }

    .action-button {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background-color: #262626;
        border: 1px solid #A9A9A9;

        display: flex;
        align-items: center;
        justify-content: center;

        position: relative;
        cursor: pointer;
    }

    .action-button:hover,
    .action-button.active {
        background-color: #A9A9A9;
    }

    .action-button img {
        width: 24px;
        height: 24px;
        position: absolute;
    }

    .icon-hover {
        opacity: 0;
    }

    .action-button:hover .icon-hover,
    .action-button.active .icon-hover {
        opacity: 1;
    }

    .action-button:hover .icon-default,
    .action-button.active .icon-default {
        opacity: 0;
    }

    .audio-controls {
        position: absolute;
        left: 10px;
        bottom: 10px;
        padding: 10px;
        border-radius: 15px;
        display: flex;
        align-items: center;
        gap: 10px;
        z-index: 100;
        border: 1px solid #A9A9A9;
        background-color: #313131;
    }

    .audio-btn img {
        width: 24px;
        height: 24px;
        display: block;
        object-fit: contain;
    }

    .audio-btn {
        background: none;
        border: none;
        cursor: pointer;
    }

    .volume-slider {
        -webkit-appearance: none;
        appearance: none;
        width: 80px;
        height: 10px;
        background: #444444;
        border-radius: 5px;
        outline: none;
        cursor: pointer;
        margin: 0;
    }

    .volume-slider::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 14px;
        height: 14px;
        background: #A9A9A9;
        border-radius: 50%;
        cursor: pointer;
        border: none;
        transition: transform 0.1s ease;
    }

    .volume-slider::-moz-range-thumb {
        width: 14px;
        height: 14px;
        background: #A9A9A9;
        border-radius: 50%;
        cursor: pointer;
        border: none;
    }

    .volume-slider:hover::-webkit-slider-thumb {
        transform: scale(1.2);
    }

    .menu-btn {
        position: absolute;
        top: 10px;
        right: 10px;
        z-index: 1000;
        background: #313131;
        border: 1px solid #A9A9A9;
        padding: 10px;
        border-radius: 5px;
        cursor: pointer;
        font-weight: 700;
        color: #A9A9A9;
    }

    .menu-btn:hover {
        background: #A9A9A9;
        border: 1px solid #A9A9A9;
        cursor: pointer;
        color: #313131;
    }

    .menu-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: rgba(0, 0, 0, 0.85);
        display: none;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        z-index: 2000;
    }

    .menu-overlay.active {
        display: flex;
    }

    .menu-content {
        background: #262626;
        padding: 25px;
        border-radius: 10px;
        border: 2px solid #A9A9A9;
        text-align: center;
    }

    .restart-btn {
        background: #e64141;
        color: white;
        border: none;
        width: 100px;
        height: 35px;
        font-size: 16px;
        cursor: pointer;
        border-radius: 5px;
        margin: 10px;
        font-weight: 700;
    }

    .restart-btn:hover {
        background: #ec1515;
    }

    .close-btn {
        background: #313131;
        color: white;
        border: none;
        width: 100px;
        height: 35px;
        font-size: 16px;
        cursor: pointer;
        border-radius: 5px;
        margin: 10px;
        font-weight: 700;
    }

    .close-btn:hover {
        background: #A9A9A9;
        color: #313131;
    }

    .room-selector {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        justify-content: center;
        margin-top: 15px;
        border-top: 1px solid #A9A9A9;
        padding-top: 15px;
    }

    .room-btn {
        background: #313131;
        color: white;
        border: none;
        padding: 12px 16px;
        font-size: 12px;
        cursor: pointer;
        border-radius: 5px;
        font-weight: 600;
    }

    .room-btn:hover {
        background: #A9A9A9;
        color: #262626;
    }
`;

/**
 * Represents the Canvas page
 */
export class CanvasComponent extends HTMLElement {
    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    private _mainActions: ActionReference[] = [];
    private _isMenuOpen: boolean = false;

    /** Current game state */
    private _currentGameState?: DefaultGameState;
    /** Current active action button */
    private _selectedActionButton?: ActionReference;
    /** Current active game object buttons */
    private _selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });
        this._backgroundMusic.loop = true;
        void this.refreshGameState();
    }

    /**
     * Refresh the current game state
     */
    private async refreshGameState(): Promise<void> {
        const state: GameState = await this._gameRouteService.getGameState();

        this.updateGameState(state);
    }

    /**
     * Update the canvas to the provided game state
     *
     * @param state Game state to update the canvas to
     */
    private updateGameState(state: GameState): void {
        if (state.type === "switch-page") {
            this._gameEventService.switchPage(state.page as Page);

            return;
        }

        const iconsInState: ActionReference[] = state.actions.filter(a => a.icon && a.icon !== "");

        if (iconsInState.length > 0) {
            this._mainActions = iconsInState;
        }

        const isDialogue: boolean = state.actions.length > 0 && state.actions.every(a => !a.icon);

        if (!isDialogue) {
            this._selectedActionButton = undefined;
            this._selectedGameObjectButtons.clear();
        }

        this._currentGameState = state;

        if (state.roomAlias === "startup") {
            this.classList.add("page-startup");
        }
        else {
            this.classList.remove("page-startup");
        }

        if (state.roomAlias === "einde") {
            this.classList.add("page-einde");
        }
        else {
            this.classList.remove("page-einde");
        }

        if (state.roomAlias) {
            this.updateMusic(state.roomAlias);
        }

        if ("sfx" in state && typeof state.sfx === "string") {
            this.playSFX(state.sfx);
        }

        this.render();
    }

    /**
     * Render the contents of this page
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const showMenuBtn: boolean = this._currentGameState?.roomAlias !== "startup";

        const elements: HTMLElement[] = htmlArray`
            <style>${styles}</style>

            ${showMenuBtn ? "<button class=\"menu-btn\">MENU</button>" : ""}

            <div class="menu-overlay ${this._isMenuOpen ? "active" : ""}">
                <div class="menu-content">
                    <h2>MENU</h2>
                        <p>Weet je zeker dat je opnieuw wilt beginnen?</p>
                            <button class="restart-btn">Herstart</button>
                            <button class="close-btn">Terug</button>

                    <div class="room-selector">
                        <p style="width: 100%; font-size: 14px; color: white;">Kamers</p>
                            <button class="room-btn" data-room="snackbar">Snackbar</button>
                            <button class="room-btn" data-room="woonkamer">Verzamelplek</button>
                            <button class="room-btn" data-room="hoofdkantoor">Hoofdkantoor</button>
                            <button class="room-btn" data-room="incasso">Incassobureau</button>
                            <button class="room-btn" data-room="casino">Casino</button>
                            <button class="room-btn" data-room="conflict">Conflict</button>
                            <button class="room-btn" data-room="MinisterieRoom">Ministerie</button>
                            <button class="room-btn" data-room="schuurtje">Schuurtje</button>
                            <button class="room-btn" data-room="gevecht">Gevecht</button>
                            <button class="room-btn" data-room="rechtbank">Rechtbank</button>
                    </div>
                </div>
            </div>

            ${this.renderAudioControls()} 
            ${this.renderTitle()}
            ${this.renderHeader()}
            ${this.renderContent()}
            ${this.renderFooter()}
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...elements);

        this.shadowRoot.querySelector(".menu-btn")?.addEventListener("click", () => {
            this._isMenuOpen = true;
            this.render();
        });

        this.shadowRoot.querySelector(".close-btn")?.addEventListener("click", () => {
            this._isMenuOpen = false;
            this.render();
        });

        this.shadowRoot.querySelector(".restart-btn")?.addEventListener("click", () => {
            void (async () => {
                try {
                    const newState: GameState = await this._gameRouteService.restartGame();
                    this._isMenuOpen = false;
                    this.updateGameState(newState);
                }
                catch (error) {
                    console.error("Herstarten mislukt:", error);
                }
            })();
        });

        this.shadowRoot.querySelectorAll(".room-btn").forEach(btn => {
            btn.addEventListener("click", e => {
                const roomAlias: string | undefined = (e.target as HTMLButtonElement).dataset.room;

                if (roomAlias) {
                    void (async () => {
                        try {
                            const newState: GameState = await this._gameRouteService.switchRoom(roomAlias);
                            this._isMenuOpen = false;
                            this.updateGameState(newState);
                        }
                        catch (error) {
                            console.error("Switch room mislukt:", error);
                        }
                    })();
                }
            });
        });

        const startBtn: Element | null = this.shadowRoot.querySelector(".btn-start-game");

        if (startBtn) {
            startBtn.addEventListener("click", () => {
                void this.executeSimpleAction("start-game");
            });
        }
    }

    /**
     * Render the title element
     *
     * @returns String with raw HTML for the title element. Can be empty.
     */
    private renderTitle(): string {
        const roomName: string | undefined = this._currentGameState?.roomName;

        if (roomName) {
            return `<div class="title">${roomName}</div>`;
        }

        return "";
    }

    /**
     * Render the header element
     *
     * @returns String with raw HTML for the header element. Can be empty.
     */
    private renderHeader(): string {
        const roomImages: string[] | undefined = this._currentGameState?.roomImages;

        if (roomImages && roomImages.length > 0) {
            return `
            <div class="header">
                ${roomImages.map(file => {
                if (file.toLowerCase().endsWith(".mp4")) {
                    return `
                            <video autoplay style="width: 50%;">
                                <source src="/assets/img/rooms/${file}" type="video/mp4">
                                Jouw browser ondersteunt geen video.
                            </video>
                        `;
                }

                return `<img src="/assets/img/rooms/${file}.png" />`;
            }).join("")}
            </div>
        `;
        }

        return "";
    }

    /**
     * Render the content element
     *
     * @returns String with raw HTML for the content element
     */
    private renderContent(): string {
        const isStartup: boolean = this._currentGameState?.roomAlias === "startup";

        if (isStartup) {
            return `
                <div class="content">
                    <button class="btn-start-game">Start</button>
                </div>
            `;
        }

        return `
            <div class="content">
                ${this._currentGameState?.text.map(text => `<p>${text}</p>`).join("") || ""}
            </div>
        `;
    }

    private async executeSimpleAction(alias: string): Promise<void> {
        const state: GameState | undefined = await this._gameRouteService.executeAction(alias);

        if (state) {
            this.updateGameState(state);
        }
    }

    /**
     * Render the footer element
     *
     * @returns HTML element of the footer
     */
    private renderFooter(): HTMLElement {
        if (this._currentGameState?.roomAlias === "einde") {
            return html`<span></span>`;
        }

        const selectedActionButton: ActionReference | undefined = this._selectedActionButton;
        const dialogueChoices: ActionReference[] = this._currentGameState?.actions.filter(a => !a.icon) || [];
        const isInDialogue: boolean = dialogueChoices.length > 0;

        return html`
        <div class="footer ${isInDialogue ? "in-dialogue" : ""}">
            <div class="buttons">
                <div>
                    ${this._mainActions.map(button => this.renderActionButton(button))}
                </div>
                <div>
                    ${isInDialogue
                        ? dialogueChoices.map(button => this.renderChoiceButton(button))
                        : selectedActionButton
                            ? this._currentGameState?.objects.filter(obj => {
                                return obj.getActions.includes(selectedActionButton.alias);
                            }).map(obj => this.renderGameObjectButton(obj)) || ""
                            : ""
                    }
                </div>
            </div>
        </div>
    `;
    }

    /**
    * Nieuwe methode voor het renderen van de dialoog-tekstkeuzes
    */
    private renderChoiceButton(button: ActionReference): HTMLElement {
        const element: HTMLElement = html`
        <a class="button">
            ${button.name}
        </a>
    `;

        element.addEventListener("click", () => void this.handleClickAction(button));

        return element;
    }

    /**
     * Render an action button for a given action reference
     *
     * @returns HTML element of the action button
     */
    private renderActionButton(button: ActionReference): HTMLElement {
        const element: HTMLElement = html`
        <a class="action-button ${this._selectedActionButton === button ? "active" : ""}" title="${button.name}">
            <img class="icon-default" src="${button.icon}" alt="${button.name}">
            <img class="icon-hover" src="${button.iconHover}" alt="${button.name}">
        </a>
    `;

        element.addEventListener("click", () => void this.handleClickAction(button));

        return element;
    }

    /**
     * Render a game object button for a given game object reference
     *
     * @returns HTML element of the game object button
     */
    private renderGameObjectButton(button: GameObjectReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="button ${this._selectedGameObjectButtons.has(button) ? "active" : ""}">
                ${button.name}
            </a>
        `;

        element.addEventListener("click", () => void this.handleClickGameObject(button));

        return element;
    }

    /**
     * Handle the click on an action button
     *
     * @param button Action button that was clicked
     */
    private async handleClickAction(button: ActionReference): Promise<void> {
        const isDialogueActive: boolean = this._currentGameState?.actions.some(a => !a.icon) || false;

        if (isDialogueActive && button.icon) {
            return;
        }

        if (button.needsObject) {
            this._selectedActionButton = button;
            this._selectedGameObjectButtons.clear();
            this.render();

            return;
        }

        const state: GameState | undefined = await this._gameRouteService.executeAction(button.alias);

        if (state === undefined) {
            return;
        }

        this.updateGameState(state);
    }

    /**
     * Handle the click on a game object button
     *
     * @param button Game object button that was clicked
     */
    private async handleClickGameObject(button: GameObjectReference): Promise<void> {
        // If no action button was clicked, do not try to handle this click.
        if (!this._selectedActionButton) {
            return;
        }

        // Add the game object to list of selected game objects
        this._selectedGameObjectButtons.add(button);

        // Try to execute the action with all game objects on the list
        const state: GameState | undefined = await this._gameRouteService.executeAction(
            this._selectedActionButton.alias,
            [...this._selectedGameObjectButtons].map(e => e.alias)
        );

        // If 2 more game objects where on the list, clear it.
        if (this._selectedGameObjectButtons.size >= 2) {
            this._selectedActionButton = undefined;
            this._selectedGameObjectButtons.clear();
        }

        // Refresh the web component
        this.render();

        // If no state was returned, exit silently. This can happen when an action needs more than 1 game object.
        if (state === undefined) {
            return;
        }

        // Otherwise, update the game state.
        this.updateGameState(state);
    }

    private _backgroundMusic: HTMLAudioElement = new Audio();
    private _currentMusicPath: string = "";

    /**
     * Speelt een geluidseffect af
     */
    private playSFX(name: string, volume: number = 1.0): void {
        const sfx: HTMLAudioElement = new Audio(`/assets/audio/sfx/${name}.mp3`);
        sfx.volume = volume;
        void sfx.play();
    }

    /**
     * Regelt de achtergrondmuziek
     */
    private updateMusic(roomAlias: string): void {
        let musicFile: string = "";

        switch (roomAlias) {
            case "startup": musicFile = "turbo_mix.mp3";
                break;
            case "snackbar": musicFile = "broodje_bakpao.mp3";
                break;
            case "woonkamer": musicFile = "turbo.mp3";
                break;
            case "hoofdkantoor": musicFile = "rave_on.mp3";
                break;
            case "incasso": musicFile = "luv_u_more.mp3";
                break;
            case "casino": musicFile = "ik_ben_ronnie.mp3";
                break;
            case "conflict": musicFile = "rainbow_in_the_sky.mp3";
                break;
            case "MinisterieRoom": musicFile = "the_promised_land.mp3";
                break;
            case "schuurtje": musicFile = "friends_turbo.mp3";
                break;
            case "gevecht": musicFile = "turbo_mix.mp3";
                break;
            case "rechtbank": musicFile = "groeten_uit_brabant.mp3";
                break;
            default:
                this._backgroundMusic.pause();
                this._backgroundMusic.src = "";
                this._currentMusicPath = "";
                this.render();

                return;
        }

        const newPath: string = `/assets/audio/music/${musicFile}`;

        if (this._currentMusicPath !== newPath) {
            this._backgroundMusic.pause();
            this._backgroundMusic.currentTime = 0;

            this._backgroundMusic.src = newPath;
            this._backgroundMusic.load();
            this._currentMusicPath = newPath;

            this._backgroundMusic.play().then(() => {
                this.render();
            }).catch((error: unknown) => {
                if (error instanceof Error) {
                    console.warn("Audio afspelen mislukt:", error.message);
                }
                else {
                    console.warn("Onbekende audio fout opgetreden");
                }

                this.render();
            });
        }
    }

    public disconnectedCallback(): void {
        this._backgroundMusic.pause();
        this._backgroundMusic.src = "";
        this._backgroundMusic.load();
    }

    private renderAudioControls(): HTMLElement {
        if (this._currentGameState?.roomAlias === "einde") {
            return html`<span></span>`;
        }

        const isPaused: boolean = this._backgroundMusic.paused;
        const volumePercentage: number = this._backgroundMusic.volume * 100;

        const getIconPath: (paused: boolean, vol: number) => string = (paused: boolean, vol: number): string => {
            if (paused) {
                return "/assets/icons/pause.svg";
            }

            if (vol === 0) {
                return "/assets/icons/volume1.svg";
            }

            if (vol <= 33) {
                return "/assets/icons/volume2.svg";
            }

            if (vol <= 66) {
                return "/assets/icons/volume3.svg";
            }

            return "/assets/icons/volume4.svg";
        };

        const container: HTMLElement = html`
            <div class="audio-controls">
                <button class="audio-btn" title="${isPaused ? "Afspelen" : "Pauzeren"}">
                    <img src="${getIconPath(isPaused, volumePercentage)}" alt="Audio status">
                </button>
                <input 
                    type="range" 
                    class="volume-slider" 
                    min="0" 
                    max="100" 
                    value="${volumePercentage}"
                >
            </div>
        `;

        const audioBtn: HTMLButtonElement = container.querySelector(".audio-btn") as HTMLButtonElement;
        const iconImg: HTMLImageElement = audioBtn.querySelector("img") as HTMLImageElement;
        const slider: HTMLInputElement = container.querySelector(".volume-slider") as HTMLInputElement;

        audioBtn.addEventListener("click", (e: Event) => {
            e.stopPropagation();

            if (this._backgroundMusic.paused) {
                void this._backgroundMusic.play();
            }
            else {
                this._backgroundMusic.pause();
            }

            this.render();
        });

        slider.addEventListener("input", (e: Event) => {
            e.stopPropagation();
            const target: HTMLInputElement = e.target as HTMLInputElement;
            const newVol: number = parseInt(target.value);

            this._backgroundMusic.volume = newVol / 100;
            iconImg.src = getIconPath(this._backgroundMusic.paused, newVol);
        });

        return container;
    }
}
